import { Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { SourceMediaSettingComponent } from '@go-modules/components/sidepanels/activity-editor/source-media-setting/source-media-setting.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { activityTemplateModelToken } from 'go-modules/models/activity-template/activity-template.factory';
import { ActivityTemplateModel } from 'go-modules/models/activity-template/activity-template.schema';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { GoCdkPopoverDirective } from '@go-modules/directives/go-cdk-popover/go-cdk-popover.directive';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { Activity } from '@go-modules/interfaces/activity';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { FocusOnFirstInvalidFieldModule } from '@go-modules/directives/focus-on-first-invalid-field/focus-on-first-invalid-field.module';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';

type CommentPrivacyOptionKey = 'PRIVATE' | 'OPEN_FEEDBACK';

interface CommentPrivacyOption {
	key: CommentPrivacyOptionKey;
	titleKey: string;
	descriptionKey: string;
	publicFeedback: boolean;
}

const COMMENT_PRIVACY_OPTIONS: CommentPrivacyOption[] = [
	{
		key: 'PRIVATE',
		titleKey: 'activity-editor-new_private',
		descriptionKey: 'activity-editor-new_private-description-filtered',
		publicFeedback: false
	},
	{
		key: 'OPEN_FEEDBACK',
		titleKey: 'activity-editor-new_everyone-sees-everything',
		descriptionKey: 'activity-editor-new_everyone-sees-everything-description-filtered',
		publicFeedback: true
	}
];

@Component({
	selector: 'present-task-additional-settings',
	templateUrl: './present-task-additional-settings.component.html',
	styleUrls: ['./present-task-additional-settings.component.scss'],
	standalone: true,
	imports: [
		SourceMediaSettingComponent,
		MatCheckboxModule,
		ReactiveFormsModule,
		TranslateModule,
		GoCdkPopoverDirective,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatIconModule,
		FocusOnFirstInvalidFieldModule
	]
})
export class PresentTaskAdditionalSettingsComponent implements OnInit, OnDestroy {
	@Output() public tasksUpdated = new EventEmitter<Task[]>();
	@ViewChild('validationButton', { read: ElementRef }) private validationButton: ElementRef<HTMLButtonElement>;
	public readonly taskType = TaskType.Present;
	public activity: ActivityModel;
	public template: ActivityTemplateModel;
	public form: FormGroup;
	public isTimeLimitEnabledControl: FormControl<boolean | null>;
	public sourceMediaControl: FormControl;
	public hasSelfSelectSourceMediaControl: FormControl;
	public sourceTypeControl: FormControl<string | null>;
	public privacyControl = new FormControl<CommentPrivacyOptionKey | null>(null);
	public commentPrivacyOptions = COMMENT_PRIVACY_OPTIONS;
	private rawActivity: Activity;
	private destroy$ = new Subject<void>();
	private currentTasks: Task[] = [];

	constructor (
		private activityEditModeService: ActivityEditModeService,
		@Inject(activityTemplateModelToken) private ActivityTemplateModel,
		@Inject(activityToken) private ActivityModel,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		private fb: FormBuilder,
		private taskService: NgxTaskService,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null
	) {
		this.sourceMediaControl = new FormControl(null);
		this.hasSelfSelectSourceMediaControl = new FormControl(false);
		this.sourceTypeControl = new FormControl<string | null>(null, [Validators.required]);

		this.form = this.fb.group({
			live_session_enabled: [false],
			is_conversation: [false],
			is_time_limit_enabled: [false],
			time_limit: [null],
			has_single_recording_attempt: [false],
			is_slides_enabled: [false],
			is_video_share_enabled: [false],
			is_video_seeking_disabled: [false],
			is_source_media_cc_disabled: [false],
			source_type: this.sourceTypeControl,
			source_media: this.sourceMediaControl
		});
		this.isTimeLimitEnabledControl = this.form.get('is_time_limit_enabled') as FormControl<boolean | null>;
	}

	public ngOnInit (): void {
		this.initializeForm();
		this.loadCurrentTasks();

		this.form.get('is_conversation').valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((enabled: boolean) => {
				this.handleGroupRecordingToggle(enabled);
			});

		this.form.get('live_session_enabled').valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((enabled: boolean) => {
				this.handleSubmitTaskAvailabilityChange(enabled);
			});

		this.form.get('has_single_recording_attempt').valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((enabled: boolean) => {
				this.handleSubmitTaskAvailabilityChange(enabled, true);
			});

		this.form.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.updateActivityFromForm();
			});

		this.privacyControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (value == null) {
					return;
				}
				const option = this.commentPrivacyOptions.find((item) => item.key === value);
				if (option == null) {
					return;
				}
				this.applyActivityChanges({
					public_feedback: option.publicFeedback,
					peer_review: false
				});
				if (option.publicFeedback) {
					this.removeReviewTaskFromEditedTasks();
				}
			});

		this.hasSelfSelectSourceMediaControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.updateActivityFromForm();
			});

		if (this.editorContext == null) {
			this.activityEditModeService.changesReverted$
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					const rawActivity = this.selectedService.getActivity();
					if (rawActivity) {
						this.initializeForm(rawActivity);
						this.resetCommentPrivacyControl(rawActivity);
					}
					this.loadCurrentTasks();
				});
		}

		if (this.shouldShowSourceMediaSetting() && this.activity.template.slug === 'stimulus_response') {
			this.sourceTypeControl.setValidators([Validators.required]);
			this.sourceMediaControl.clearValidators();
		} else if (this.shouldShowSourceMediaSetting() && this.activity.template.slug === 'feedback_only') {
			this.sourceMediaControl.setValidators([Validators.required]);
			this.sourceTypeControl.clearValidators();
		} else {
			this.sourceTypeControl.clearValidators();
			this.sourceMediaControl.clearValidators();
		}
		this.sourceTypeControl.updateValueAndValidity({ emitEvent: false });
		this.sourceMediaControl.updateValueAndValidity({ emitEvent: false });

		this.registerAdditionalSettingsValidator();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public shouldShowSourceMediaSetting (): boolean {
		// APED configures source media in Activity Details. The legacy editor has no shared
		// details surface, so it still needs the task-level source media setting.
		return this.editorContext == null &&
			this.activity.template.source_media === 'write';
	}

	public shouldShowCommentPrivacySetting (): boolean {
		return this.template.isNone('peer_review')
			&& this.template.isRead('public_feedback');
	}

	public getSelectedCommentPrivacyOption (): CommentPrivacyOption | undefined {
		return this.commentPrivacyOptions.find((option) => option.key === this.privacyControl.value);
	}

	public shouldShowVideoShareSetting (): boolean {
		if (!this.activity.isViewable('is_video_share_enabled')) {
			return false;
		}

		if (this.editorContext != null) {
			const orgSettings = this.editorContext.orgSettings?.() ?? null;
			return !!orgSettings?.allow_video_share;
		}

		const orgSettings = this.selectedService.getOrg().org_settings;
		return !!orgSettings?.allow_video_share;
	}

	public validateAdditionalSettingsForm (): boolean {
		if (!this.form.valid) {
			this.focusFirstInvalidField();
			return false;
		}
		return true;
	}

	public focusFirstInvalidField (): void {
		this.validationButton.nativeElement.click();
	}

	private initializeForm (activityOverride?: Activity): void {
		const rawActivity = activityOverride ?? this.getCurrentActivity();
		if (rawActivity == null) {
			return;
		}

		this.rawActivity = rawActivity;
		this.activity = this.ActivityModel.model(rawActivity);
		this.template = this.ActivityTemplateModel.model(this.activity.template);

		const isConversation = this.rawActivity.is_conversation ?? false;
		// Template rules clear time_limit with false, which the legacy integer transformer
		// represents as NaN. Only a positive finite value means the setting is enabled.
		const hasTimeLimit = typeof this.rawActivity.time_limit === 'number' && Number.isFinite(this.rawActivity.time_limit) &&
			this.rawActivity.time_limit > 0;
		this.form.patchValue({
			live_session_enabled: this.rawActivity.live_session_enabled ?? false,
			is_conversation: isConversation,
			is_time_limit_enabled: hasTimeLimit,
			time_limit: hasTimeLimit ? this.rawActivity.time_limit : null,
			has_single_recording_attempt: this.rawActivity.has_single_recording_attempt ?? false,
			is_slides_enabled: this.rawActivity.is_slides_enabled ?? false,
			is_video_share_enabled: this.rawActivity.is_video_share_enabled ?? false,
			is_video_seeking_disabled: this.rawActivity.is_video_seeking_disabled ?? false,
			is_source_media_cc_disabled: this.rawActivity.is_source_media_cc_disabled ?? false
		}, { emitEvent: false });

		if (isConversation) {
			this.form.get('is_time_limit_enabled').disable({emitEvent: false});
			this.form.get('time_limit').disable({emitEvent: false});
			this.form.get('has_single_recording_attempt').disable({emitEvent: false});
			this.form.get('is_slides_enabled').disable({emitEvent: false});
		}

		this.sourceMediaControl.setValue(this.activity.source_media, { emitEvent: false });
		this.hasSelfSelectSourceMediaControl.setValue(this.activity.has_self_select_source_media, { emitEvent: false });

		this.resetCommentPrivacyControl(this.rawActivity);
	}

	private updateActivityFromForm (): void {
		if (!this.rawActivity) {
			return;
		}

		const formValues = this.form.value;
		const updatedActivity = {
			...this.rawActivity,
			live_session_enabled: formValues.live_session_enabled,
			is_conversation: formValues.is_conversation,
			time_limit: formValues.is_time_limit_enabled ? (formValues.time_limit ?? null) : null,
			// value is undefined if control is disabled so set it to false
			has_single_recording_attempt: formValues.has_single_recording_attempt ?? false,
			is_slides_enabled: formValues.is_slides_enabled ?? false,
			is_video_share_enabled: formValues.is_video_share_enabled ?? false,
			is_video_seeking_disabled: formValues.is_video_seeking_disabled ?? false,
			is_source_media_cc_disabled: formValues.is_source_media_cc_disabled ?? false,
			source_media: this.sourceMediaControl.value,
			has_self_select_source_media: this.hasSelfSelectSourceMediaControl.value
		} as Activity;

		this.rawActivity = updatedActivity;
		this.updateActivity(updatedActivity);
	}

	private applyActivityChanges (changes: Partial<Activity>): void {
		if (!this.rawActivity) {
			return;
		}

		const updatedActivity = {
			...this.rawActivity,
			...changes
		} as Activity;

		this.rawActivity = updatedActivity;
		this.activity = this.ActivityModel.model(updatedActivity);
		this.updateActivity(updatedActivity);
	}

	private getCommentPrivacyOptionKey (activity: Activity): CommentPrivacyOptionKey {
		const found = this.commentPrivacyOptions.find((option) =>
			option.publicFeedback === !!activity.public_feedback
		);

		return (found?.key ?? COMMENT_PRIVACY_OPTIONS[0].key) as CommentPrivacyOptionKey;
	}

	private resetCommentPrivacyControl (activity: Activity): void {
		if (this.shouldShowCommentPrivacySetting()) {
			this.privacyControl.setValue(this.getCommentPrivacyOptionKey(activity), { emitEvent: false });
			return;
		}

		this.privacyControl.setValue(null, { emitEvent: false });
	}

	private loadCurrentTasks (): void {
		if (this.editorContext != null) {
			this.currentTasks = [...this.editorContext.tasks()];
			return;
		}

		const activity = this.activityEditModeService.currentActivity();
		if (activity == null || !activity.activity_id) {
			return;
		}

		const editedTasks = this.activityEditModeService.currentTasks();
		if (editedTasks && editedTasks.length > 0) {
			this.currentTasks = [...editedTasks];
			return;
		}

		this.taskService.getTasks(activity.activity_id)
			.pipe(takeUntil(this.destroy$))
			.subscribe((tasks) => {
				this.currentTasks = [...tasks];
				this.activityEditModeService.updateEditedTasks(this.currentTasks);
			});
	}

	private updateTasks (tasks: Task[]): void {
		this.currentTasks = [...tasks];
		this.updateEditedTasks(this.currentTasks);
		this.tasksUpdated.emit(this.currentTasks);
	}

	private handleGroupRecordingToggle (enabled: boolean): void {
		const controlsToToggle = ['is_time_limit_enabled', 'time_limit', 'has_single_recording_attempt', 'is_slides_enabled'];

		if (enabled) {
			controlsToToggle.forEach((controlName) => {
				const control = this.form.get(controlName);
				if (controlName === 'time_limit') {
					control.setValue(null, { emitEvent: false });
				} else {
					control.setValue(false, { emitEvent: false });
				}
				control.disable({ emitEvent: false });
			});
		} else {
			controlsToToggle.forEach((controlName) => {
				this.form.get(controlName).enable({ emitEvent: false });
			});
		}
	}

	private handleSubmitTaskAvailabilityChange (enabled: boolean, restoreSubmitAfterPresent = false): void {
		const editedTasks = this.getCurrentTasks();
		const currentTasks = editedTasks && editedTasks.length > 0 ? [...editedTasks] : [...this.currentTasks];

		if (enabled) {
			const submitTask = currentTasks.find((task) => task.type === TaskType.Submit);
			if (submitTask) {
				const clonedSubmitTask = structuredClone(submitTask);
				clonedSubmitTask.deleted_at = new Date().toISOString();
				clonedSubmitTask.deleted_by = this.userService.currentUser.user_id;
				this.updateTasks([...currentTasks.filter((task) => task.type !== TaskType.Submit), clonedSubmitTask]);
			}
		} else {
			const submitTask = currentTasks.find((task) => task.type === TaskType.Submit);
			if (submitTask?.deleted_at) {
				const clonedSubmitTask = structuredClone(submitTask);
				clonedSubmitTask.deleted_at = null;
				clonedSubmitTask.deleted_by = null;
				const updatedTasks = currentTasks.filter((task) => task.type !== TaskType.Submit);
				if (restoreSubmitAfterPresent) {
					const presentTaskIndex = updatedTasks.findIndex((task) => task.type === TaskType.Present);
					updatedTasks
						.splice(
							presentTaskIndex >= 0 ? presentTaskIndex + 1 : updatedTasks.length, 0, clonedSubmitTask
						);
				} else {
					updatedTasks.push(clonedSubmitTask);
				}
				this.updateTasks(updatedTasks);
			}
		}
	}

	private removeReviewTaskFromEditedTasks (): void {
		const editedTasks = this.getCurrentTasks();
		const currentTasks = editedTasks && editedTasks.length > 0 ? [...editedTasks] : [...this.currentTasks];
		const hasReview = currentTasks.some((task) => task.type === TaskType.Review);
		if (!hasReview) {
			return;
		}

		this.updateTasks(currentTasks.filter((task) => task.type !== TaskType.Review));
	}

	private getCurrentActivity (): Activity | null {
		if (this.editorContext != null) {
			return this.editorContext.activity();
		}

		return this.activityEditModeService.currentActivity();
	}

	private getCurrentTasks (): Task[] {
		return this.editorContext?.tasks() ?? this.activityEditModeService.currentTasks();
	}

	private updateActivity (activity: Activity): void {
		if (this.editorContext != null) {
			this.editorContext.updateActivity(activity);
			return;
		}

		this.activityEditModeService.updateEditedActivity(activity);
	}

	private updateEditedTasks (tasks: Task[]): void {
		if (this.editorContext != null) {
			this.editorContext.updateTasks(tasks);
			return;
		}

		this.activityEditModeService.updateEditedTasks(tasks);
	}

	private registerAdditionalSettingsValidator (): void {
		if (this.editorContext != null) {
			this.editorContext.registerAdditionalSettingsValidator(this);
			return;
		}

		this.activityEditModeService.registerAdditionalSettingsValidator(this);
	}
}
